import React, { ReactNode } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import type { FormattedString } from '../../types';
import { RCTag } from '../../RCTag';
import { RCTextWithIcon } from '../../RCTextWithIcon';
import ExclamationTriangleIcon from '../../../../design-system/icons/core/exclamation-triangle.svg?component';

type TagType = 'none' | 'required' | 'optional' | 'recommended';

interface Props {
  /** Main label text (or more complex components if you really need to) */
  label: FormattedString;

  /** Single RC input component */
  children: ReactNode;
  /** ID of the input component this wraps */
  inputId: string;
  /** Validation error message */
  errorText?: string;
  /** Additional contextual info for this input */
  helpText?: FormattedString;
  large?: boolean;
  /** Annotation on the field, e.g. 'required', 'optional' */
  tag?: TagType;
}

function HelpText({ children }: { children: ReactNode }) {
  return (
    <div className="rc-form-group__help-text">
      {children}
    </div>
  );
}

export function RCFormGroupAlertText({ errorText }: { errorText: string }) {
  return (
    <div className="rc-form-group__error">
      <RCTextWithIcon svgComponent={ExclamationTriangleIcon} placement="left">
        {errorText}
      </RCTextWithIcon>
    </div>
  );
}

export function RCFormGroup({ tag = 'none', ...props }: Props) {
  const classes = classNames(
    'rc-form-group',
    { 'rc-form-group--with-error': !!props.errorText },
    { 'rc-form-group--large': props.large },
  );

  return (
    <div className={classes}>
      <div className="rc-form-group__header">
        <label
          htmlFor={props.inputId}
          className="rc-form-group__label"
        >
          {props.label}
        </label>
        {tag && tag !== 'none' && (
          <div className="rc-form-group__tag">
            <RCTag variant={tag === 'recommended' ? 'highlight' : null}>
              {I18n.t(`commons.formGroup.${tag}`)}
            </RCTag>
          </div>
        )}
      </div>
      {props.large && props.helpText && (
        <HelpText>{props.helpText}</HelpText>
      )}
      <div className="rc-form-group__fields">
        {props.children}
      </div>
      {!props.large && !!props.helpText && (
        <HelpText>{props.helpText}</HelpText>
      )}
      {props.errorText && <RCFormGroupAlertText errorText={props.errorText} />}
    </div>
  );
}
